import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddIcon from '@mui/icons-material/Add';
import { Box, useTheme, IconButton, Stack, ThemeProvider } from '@mui/material';
import { getDefaultAlertProps, unixToLocal } from '../../utils/utils';
import { MyAlert } from '../UI/MyAlert';
import { IAlertProps } from '../../interface/IAlertProps';
import {
  MAIN_COLOR_STRING,
  PAGE_SIZE_DEVICES,
  START_PAGE_SIZE_DEVICES,
} from '../../constant/constants';
import { BackDropDiv } from '../backdrop/BackDropDiv';
import { IApiResult } from '../../interface/IApiResult';
import { AlertSeverityEnum } from '../../enum/AlertSeverityEnum';
import { MyTextFiled } from '../UI/MyTextField';
import { FactoryBodyHeaderDiv } from '../body-header/FactoryBodyHeaderDiv';
import { LocalServerDto2 } from '../../dto/LocalServerDto2';
import { apiGetLocalServer2 } from '../../service/local-server-license-2/apiGetLocalServer2';
import { apiDeleteLocalServer2 } from '../../service/local-server-license-2/apiDeleteLocalServer2';
import { apiAddLocalServer2 } from '../../service/local-server-license-2/apiAddLocalServer2';
import { MyButton } from '../UI/MyButton';

export const LocalServerDiv2 = () => {
  const [pageSize, setPageSize] = React.useState(START_PAGE_SIZE_DEVICES);
  const { t } = useTranslation();
  const [backDrop, setBackDrop] = useState(false);
  const [alertProps, setAlertProps] = useState<IAlertProps>(
    getDefaultAlertProps()
  );
  const [localServer2Dto, setLocalServer2Dto] = useState<LocalServerDto2>({
    id: -1,
    serverId: '',
    name: '',
    organization: '',
    dateFrom: -1,
    dateTo: -1,
    cnt: 0,
    license: '',
    note: '',
  });
  const [localServer2Dtos, setLocalServer2Dtos] = useState<LocalServerDto2[]>(
    []
  );

  const getLocalServer2 = async () => {
    setBackDrop(true);
    const apiResult: IApiResult = await apiGetLocalServer2();
    if (apiResult.isSuccess) {
      setLocalServer2Dtos(apiResult.data.localServer);
    } else {
      setAlertProps({
        message: t('errotServer'),
        severity: AlertSeverityEnum.error,
      });
    }
    setBackDrop(false);
  };

  const deletLocalServer2 = async (dto: LocalServerDto2) => {
    const apiResult: IApiResult = await apiDeleteLocalServer2(dto);
    if (apiResult.isSuccess) {
      getLocalServer2();
    } else {
      setAlertProps({
        message: t('errotServer'),
        severity: AlertSeverityEnum.error,
      });
    }
  };

  const addLocalServer2 = async (dto: LocalServerDto2) => {
    const apiResult: IApiResult = await apiAddLocalServer2(dto);
    if (apiResult.isSuccess) {
      getLocalServer2();
    } else {
      setAlertProps({
        message: t('errotServer'),
        severity: AlertSeverityEnum.error,
      });
    }
  };

  const [checkHelper, setCheckHelper] = useState(false);

  const handelCreate = async () => {
    if (localServer2Dto?.serverId === '' || localServer2Dto?.name === '') {
      setCheckHelper(true);
      return;
    }
    await addLocalServer2(localServer2Dto);
  };

  const theme = useTheme();

  useEffect(() => {
    getLocalServer2();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'organization',
      headerName: t('headerServerOrganization'),
      renderHeader: () => <strong>{t('headerServerOrganization')}</strong>,
      flex: 1,
      minWidth: 150,
      editable: false,
      renderCell: (row) => row.row.organization,
    },
    {
      field: 'name',
      headerName: t('headerServerName'),
      renderHeader: () => <strong>{t('headerServerName')}</strong>,
      flex: 1,
      minWidth: 150,
      editable: false,
      renderCell: (row) => row.row.name,
    },
    {
      field: 'serverId',
      headerName: t('headerServerId'),
      renderHeader: () => <strong>{t('headerServerId')}</strong>,
      flex: 1,
      minWidth: 150,
      editable: false,
      renderCell: (row) => row.row.serverId,
    },
    {
      field: 'dateFrom',
      headerName: t('headerServerDateFrom'),
      renderHeader: () => <strong>{t('headerServerDateFrom')}</strong>,
      minWidth: 170,
      editable: false,
      renderCell: (row) => unixToLocal(row.row.dateFrom),
    },
    {
      field: 'dateTo',
      headerName: t('headerServerDateTo'),
      renderHeader: () => <strong>{t('headerServerDateTo')}</strong>,
      minWidth: 170,
      editable: false,
      renderCell: (row) => unixToLocal(row.row.dateTo),
    },
    {
      field: 'cnt',
      headerName: t('headerServerCnt'),
      renderHeader: () => <strong>{t('headerServerCnt')}</strong>,
      minWidth: 50,
      editable: false,
      renderCell: (row) => row.row.cnt,
    },
    {
      field: 'license',
      headerName: t('headerServerLicense'),
      renderHeader: () => <strong>{t('headerServerLicense')}</strong>,
      minWidth: 170,
      editable: false,
      renderCell: (row) => (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <MyButton
          text={t('tooltipCopy2')}
          width="150px"
          height="40px"
          background={MAIN_COLOR_STRING}
          onClick={() => navigator.clipboard.writeText(row.row.license)}
        />
      ),
    },
    {
      field: 'id',
      type: 'actions',
      width: 70,
      headerName: '',
      editable: false,
      sortable: false,
      filterable: false,
      renderCell: (row) => (
        <Stack direction="row" width="100%" justifyContent="center">
          <IconButton
            size="small"
            title={t('tooltipDelete')}
            onClick={() => deletLocalServer2(row.row)}
          >
            <DeleteForeverIcon
              fontSize="small"
              sx={{
                color: 'red',
              }}
            />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const other = {
    autoHeight: true,
    showCellRightBorder: true,
    showColumnRightBorder: true,
  };

  return (
    <div>
      <BackDropDiv open={backDrop} />
      <MyAlert
        message={alertProps.message}
        severity={alertProps.severity}
        onClose={() => setAlertProps({ ...alertProps, message: '' })}
      />
      <Stack width="100%" alignItems="center" marginBottom="20px" spacing={2}>
        <Stack width="90%">
          <FactoryBodyHeaderDiv title={t('localServer')} />
        </Stack>
        <Stack width="90%" spacing={1}>
          <Stack direction="row" spacing={1}>
            <MyTextFiled
              value={localServer2Dto.organization}
              autoComplete="off"
              onChange={(e) =>
                setLocalServer2Dto({
                  ...localServer2Dto,
                  organization: e.target.value,
                })
              }
              size="small"
              error={checkHelper}
              label={t('textServerOrganization')}
            />
            <MyTextFiled
              value={localServer2Dto.name}
              autoComplete="off"
              onChange={(e) =>
                setLocalServer2Dto({ ...localServer2Dto, name: e.target.value })
              }
              size="small"
              error={checkHelper}
              label={t('textServerName')}
            />
            <MyTextFiled
              value={localServer2Dto.serverId}
              onChange={(e) =>
                setLocalServer2Dto({
                  ...localServer2Dto,
                  serverId: e.target.value,
                })
              }
              autoComplete="off"
              size="small"
              error={checkHelper}
              label={t('textServerId')}
            />
            <MyTextFiled
              value={localServer2Dto.cnt}
              onChange={(e) =>
                setLocalServer2Dto({
                  ...localServer2Dto,
                  cnt: e.target.value,
                })
              }
              autoComplete="off"
              size="small"
              error={checkHelper}
              label={t('headerServerCnt')}
            />
            <Stack alignItems="center">
              <IconButton
                sx={{ color: MAIN_COLOR_STRING }}
                onClick={handelCreate}
              >
                <AddIcon />
              </IconButton>
            </Stack>
          </Stack>
          <Box
            sx={{
              '& .app-theme-cell': {
                fontFamily: 'sans-serif',
              },
            }}
          >
            <ThemeProvider theme={theme}>
              <DataGrid
                sx={{
                  '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
                    py: 1,
                    fontSize: '100px',
                  },
                  '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
                    py: '5px',
                  },
                  '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {
                    py: '10px',
                  },
                  '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus-within':
                    {
                      outline: 'none !important',
                    },
                }}
                getRowHeight={() => 'auto'}
                columns={columns}
                rows={localServer2Dtos}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...other}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={PAGE_SIZE_DEVICES}
                disableSelectionOnClick
                experimentalFeatures={{ newEditingApi: true }}
              />
            </ThemeProvider>
          </Box>
        </Stack>
      </Stack>
    </div>
  );
};
